import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ViewModelClipboardPayload } from '@view-model/application/operation/ViewModelClipboardPayload';
import type { ViewJSON } from 'balus-common';
import { GeneralModal } from '@framework/ui/elements/GeneralModal';
import { Button } from '@framework/ui/atoms/Button';

type Props = {
  isOpen: boolean;
  onSubmit(): Promise<void>;
  onClose(): void;
  payload: ViewModelClipboardPayload;
};

export const ViewModelPasteConfirmModal: React.FC<Props> = ({ isOpen, onSubmit, onClose, payload }: Props) => {
  const { t } = useTranslation();
  const { views, consistencyLinks } = payload;
  const consistencyLinkCount = Object.entries(consistencyLinks).length;
  const [pasting, setPasting] = useState(false);

  const toReadableName = (name: string): string => {
    return name === '' ? t('canvas.view.untitled') : name;
  };

  const handleSubmit = useCallback(async () => {
    setPasting(true);
    await onSubmit();
    setPasting(false);
  }, [onSubmit]);

  return (
    <GeneralModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('canvas.viewModelPaste.confirm')}
      submitButton={
        <Button onClick={handleSubmit} color="primary" loading={pasting} disabled={pasting}>
          {t('canvas.viewModelPaste.submit')}
        </Button>
      }
      cancelButton={
        <Button onClick={onClose} color="secondary" disabled={pasting}>
          {t('common.action.cancel')}
        </Button>
      }
    >
      <div className="overflow-y-auto p-4" style={{ width: '600px', maxHeight: '70vh' }}>
        {t('canvas.viewModelPaste.description')}
        <br />
        <div className="py-2">
          <div className="text-xl font-bold">{t('canvas.viewModelPaste.viewSection')}</div>
          <ul className="ml-8 list-disc">
            {views.length > 0 ? (
              views.map((view: ViewJSON) => <li key={view.key}>{toReadableName(view.name)}</li>)
            ) : (
              <li>{t('canvas.viewModelPaste.noViews')}</li>
            )}
          </ul>
        </div>
        <div className="py-2">
          <div className="text-xl font-bold">{t('canvas.viewModelPaste.consistencyLinkSection')}</div>
          <ul className="ml-8 list-disc">
            {consistencyLinkCount > 0 ? (
              <li>{t('canvas.viewModelPaste.consistencyLinkCount', { count: consistencyLinkCount })}</li>
            ) : (
              <li>{t('canvas.viewModelPaste.noConsistencyLinks')}</li>
            )}
          </ul>
        </div>
      </div>
    </GeneralModal>
  );
};
